import { useEffect, useState } from "react";
import { useTransitionNavigation } from "../../../shared/hooks/useTransitionNavigation";
import { pageTransitions } from "../../../shared/transitions/pageTransitions";
import { useSearch, type SearchResult } from "../hooks/useSearch";
import { DesktopSearchDialog } from "./DesktopSearchDialog";
import { MobileSearchDrawer } from "./MobileSearchDrawer";

const RECENT_SEARCHES_KEY = "lumi-recent-searches";
const MAX_RECENT_SEARCHES = 5;

interface SearchInputProps {
  /** Whether search overlay is open */
  readonly open: boolean;
  /** Callback when overlay should close */
  readonly onOpenChange: (open: boolean) => void;
}

/**
 * Get recent searches from localStorage
 */
function getRecentSearches(): string[] {
  try {
    const stored = localStorage.getItem(RECENT_SEARCHES_KEY);
    return stored ? (JSON.parse(stored) as string[]) : [];
  } catch {
    return [];
  }
}

/**
 * Save search query to recent searches
 */
function saveRecentSearch(query: string) {
  try {
    const recent = getRecentSearches();
    const updated = [query, ...recent.filter((q) => q !== query)].slice(0, MAX_RECENT_SEARCHES);
    localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(updated));
  } catch {
    // Ignore localStorage errors
  }
}

/**
 * SearchInput - Responsive search overlay
 * Command dialog (desktop) or drawer (mobile)
 * Keyboard shortcut: Cmd/Ctrl + K (handled by parent)
 */
export function SearchInput({ open, onOpenChange }: SearchInputProps) {
  const [isMobile, setIsMobile] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const { transitionTo } = useTransitionNavigation();
  const { results, loading, search, suggestions, getSuggestions } = useSearch({
    debounceMs: 500, // Increased from 300ms to reduce jumpiness
    suggestionsDebounceMs: 250, // Increased from 150ms to reduce jumpiness
    limit: 8,
  });

  // Detect mobile viewport
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Load recent searches
  useEffect(() => {
    if (open) setRecentSearches(getRecentSearches());
  }, [open]);

  // Execute search and get suggestions
  useEffect(() => {
    if (inputValue.trim()) {
      search(inputValue);
      getSuggestions(inputValue);
    }
  }, [inputValue, search, getSuggestions]);

  const handleResultSelect = (result: SearchResult) => {
    saveRecentSearch(inputValue);
    onOpenChange(false);
    transitionTo(`/home/title/${result.id}`, pageTransitions.fullScreenFade);
    setInputValue("");
  };

  const handleRecentSearchSelect = (query: string) => {
    setInputValue(query);
    search(query);
  };

  const handleViewAllResults = () => {
    if (inputValue.trim()) {
      saveRecentSearch(inputValue);
      onOpenChange(false);
      transitionTo(`/home/search?q=${encodeURIComponent(inputValue)}`, pageTransitions.slideHorizontal);
      setInputValue("");
    }
  };

  // Get the top title suggestion for inline autocomplete
  const autocompleteSuggestion = inputValue.trim()
    ? suggestions.find((s) => s.type === "title" && s.value.toLowerCase().startsWith(inputValue.toLowerCase()))?.value
    : undefined;

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Right arrow accepts autocomplete suggestion
    if (e.key === "ArrowRight" && autocompleteSuggestion && inputValue.length > 0) {
      const cursorAtEnd = (e.target as HTMLInputElement).selectionStart === inputValue.length;
      if (cursorAtEnd) {
        e.preventDefault();
        setInputValue(autocompleteSuggestion);
      }
    }
  };

  const sharedProps = {
    open,
    onOpenChange,
    inputValue,
    onInputChange: setInputValue,
    loading,
    results,
    recentSearches,
    onResultSelect: handleResultSelect,
    onRecentSearchSelect: handleRecentSearchSelect,
    onViewAllResults: handleViewAllResults,
    autocompleteSuggestion,
    onKeyDown: handleKeyDown,
  };

  return isMobile ? <MobileSearchDrawer {...sharedProps} /> : <DesktopSearchDialog {...sharedProps} />;
}
